import { mergeAttributes, Node } from "@tiptap/react"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		AttachmentNode: {
			setAttachment: (args: { uuid: string }) => ReturnType
			deleteAttachment: ({ uuid }: { uuid: string }) => ReturnType
		}
	}
}

export const BaseAttachmentNode = Node.create({
	name: "AttachmentNode",
	group: "block",
	selectable: true,
	addAttributes() {
		return {
			uuid: {
				default: null,
			},
		}
	},
	addCommands() {
		return {
			setAttachment:
				({ uuid }) =>
				({ chain }) => {
					return chain()
						.focus()
						.insertContent({
							type: this.type.name,
							attrs: { uuid: uuid },
						})
						.run()
				},
			deleteAttachment:
				({ uuid }) =>
				({ chain, editor }) => {
					const attachmentNode = editor.$doc.querySelector("AttachmentNode", { uuid })
					if (attachmentNode) {
						/** NOTE: there is some subtle position going on here, currently, custom nodes are rendered in the HTML as
						 * <custom-node></custom-node> even if, like in this case, no TipTap/ProseMirror content ever is contained
						 * within the AttachmentNode. It would make more sense to have the node rendering similar to an image tag
						 * (i.e  <custom-node />). Because of this, the ProseMirror positioning is off by one do to the
						 * extra closing tag token:
						 *
						 * (position x) <custom-node> (position x+1) </custom-node> (position x+2)
						 *
						 * Because of this, for some reason, the pos attribute on the Node instance returns x+1 and not x,
						 * therefore, when trying to delete this node using a range, the position before must be used
						 * to get position x
						 */
						const from = attachmentNode.before?.pos ?? 0
						const to = from + attachmentNode.size
						return chain().focus().deleteRange({ from, to }).run()
					}
					return false
				},
		}
	},
	parseHTML() {
		return [
			{
				tag: "attachment",
			},
		]
	},
	renderHTML({ HTMLAttributes }) {
		return ["attachment", mergeAttributes(HTMLAttributes)]
	},
})
